import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Form,
  Modal,
  notification,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useParams } from 'react-router-dom';

import {
  emptyValuesVariables,
  ExperimentAssignmentStrategy,
  getAssignmentStrategy,
  getVariables,
  isEditMode,
  isPercentageSplitStrategy,
} from '../constants';
import { useExperimentContext } from '../contexts/experiment-context';
import {
  getUpdateExperimentDto,
  toCreateExperimentDto,
  toExperimentDto,
  toReviewExperimentDto,
} from '../domain/experiment/transform';
import {
  AXA_WARNING_MESSAGES,
  getAxaCompatibilityInfo,
  isAxaIncompatibleSliceWindow,
  isAxaIncompatibleStartTime,
  isValidMetric,
  validPercentageChoice,
  validWeightedChoiceTotal,
} from '../utils';
import DeepDive from './metrics/deep-dive';
import { getDomainFamily } from '@/common/domains';
import { FeedbackType, openInAppFeedback } from '@/common/inAppFeedback';
import { newCreationFlowEvents } from '@/common/Scribe/EventsList';
import { useReviewExperiment } from '@/hooks';
import Service from '@/lib/App';
import { ExperimentReview } from '@/lib/PlannerDTOs';
import { useAppNavigate } from '@/utils/navigation';
import { sendScribeEvent } from '@/utils/scribe';

const ITERIM_SCORECARD_GENERATION_TIME = 72;

export const calculateScorecardDate = (startDate: dayjs.Dayjs): string => {
  try {
    if (!startDate.isValid()) {
      return 'date to be determined';
    }

    return startDate
      .add(ITERIM_SCORECARD_GENERATION_TIME, 'hour')
      .format('MMMM D, YYYY');
  } catch (error) {
    return 'date to be determined';
  }
};

const ReviewModal = ({
  open,
  onOk,
  onCancel,
  reviewResult,
}: {
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
  reviewResult: ExperimentReview | null | undefined;
}) => {
  const params = useParams<{ action: string }>();
  const { action } = params;

  const experimentContext = useExperimentContext();
  const { experiment, variables, configurations, analysis } = experimentContext;
  const experimentDomain = experiment?.domain;
  const startDate = experiment?.startTime;
  const analysisData = analysis?.analysisConfig;
  const experimentDto = toExperimentDto(experiment, configurations);
  const primaryMetrics = experiment?.metadata?.primaryMetricOptOutEnabled
    ? []
    : analysisData?.primary_metrics || [];
  const allMetrics = [
    ...primaryMetrics,
    ...(analysisData?.secondary_metrics || []),
    ...(analysisData?.guardrail_metrics || []),
  ];
  const validMetrics = allMetrics.filter((metric) => isValidMetric(metric));

  const { navigateTo } = useAppNavigate();

  const { errors, warnings, conflicts, overlappedExperiments, success } =
    reviewResult ?? {};

  const [isEmptyVariableUnchecked, setIsEmptyVariableUnchecked] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emptyVariables = emptyValuesVariables(configurations, variables);
  //   const payload = toReviewExperimentDto(experiment, analysis, configurations);

  const isDomainlessFeatureFlag = Service.cache.isFeatureFlagOn('domainless');
  const isDomainOverlapSupported =
    Service.cache.isDomainOverlapSupported(experimentDomain);

  const triggerAnalysis = analysisData?.enable_triggered_analysis;

  useEffect(() => {
    scribe.sendEvent(newCreationFlowEvents.viewReviewModal.name, {
      name: experimentDto?.name,
      errors: errors,
      warnings: warnings,
      json_view: JSON.stringify(experimentDto),
      STATE_NAME: newCreationFlowEvents.viewReviewModal.state,
    });
  }, []);

  const createExperiment = async () => {
    const payload = toCreateExperimentDto(experiment, analysis, configurations);

    try {
      await Service.planner.createExperiment(payload);
      notification.success({
        message: 'Creating an Experiment',
        description: 'Experiment was successfully created.',
      });

      navigateTo(
        `/${getDomainFamily()}/experiments/${payload?.experiment?.name}/overview`,
      );

      scribe.sendEvent(newCreationFlowEvents.submitReviewModal.name, {
        name: payload?.experiment?.name,
        errors: errors,
        warnings: warnings,
        json_view: JSON.stringify(payload),
        STATE_NAME: newCreationFlowEvents.submitReviewModal.state,
        submitStatus: 'success',
      });

      openInAppFeedback({
        type: FeedbackType.CSAT,
        context: 'expCreationFlow',
        event: 'expCreated',
      });
    } catch (e) {
      const errorData = e?.response?.data;
      scribe.sendEvent(newCreationFlowEvents.submitReviewModal.name, {
        name: payload?.experiment?.name,
        errors: errorData ? JSON.stringify(errorData) : null,
        json_view: JSON.stringify(payload),
        STATE_NAME: newCreationFlowEvents.submitReviewModal.state,
        submitStatus: 'failed',
      });
      notification.error({
        message: 'Creating an Experiment',
        description: (
          <>
            <div>Experiment failed to create</div>
            {errorData?.arg && <div>Error: {errorData.arg}</div>}
          </>
        ),
      });
      throw e;
    }
  };

  const updateExperiment = async () => {
    const payload = getUpdateExperimentDto(
      experiment,
      analysis,
      configurations,
    );

    try {
      await Service.planner.editExperiment(payload);
      notification.success({
        message: 'Edit an Experiment',
        description: 'Experiment was successfully edited.',
      });

      navigateTo(
        `/${getDomainFamily()}/experiments/${payload?.experiment?.name}/overview`,
      );
    } catch (e) {
      const errorData = e?.response?.data;
      notification.error({
        message: 'Editing an Experiment',
        description: (
          <>
            <div>Experiment failed to edit</div>
            {errorData?.arg && <div>Error: {errorData.arg}</div>}
          </>
        ),
      });
      throw e;
    }
  };

  const submitForm = async () => {
    if (isEditMode(action)) {
      await updateExperiment();
    } else {
      await createExperiment();
    }
  };

  const handleEmptyVariableClick = (e) => {
    setIsEmptyVariableUnchecked(e.target.checked);
  };

  const handleOk = async () => {
    try {
      setIsSubmitting(true);
      await submitForm();
      setIsSubmitting(false);
      onOk();
    } catch (e) {
      scribe.sendEvent(newCreationFlowEvents.submitReviewModal.name, {
        name: experimentDto?.name,
        errors: errors,
        warnings: warnings,
        json_view: JSON.stringify(experimentDto),
        STATE_NAME: newCreationFlowEvents.submitReviewModal.state,
        submitStatus: 'failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    scribe.sendEvent(newCreationFlowEvents.cancelReviewModal.name, {
      name: experimentDto?.name,
      errors: errors,
      warnings: warnings,
      json_view: JSON.stringify(experimentDto),
      STATE_NAME: newCreationFlowEvents.cancelReviewModal.state,
    });
    onCancel();
  };

  const flattenConflicts = reviewResult?.conflicts?.flatMap((conflictItem) =>
    conflictItem.experiments?.map((experiment) => experiment),
  );

  return (
    <Modal
      open={open}
      centered
      title="Experiment Review"
      onOk={handleOk}
      onCancel={handleCancel}
      okText={isEditMode(action) ? 'Save changes' : 'Submit for approval'}
      okButtonProps={{
        size: 'small',
        disabled:
          isSubmitting ||
          !success ||
          (emptyVariables.length > 0 && !isEmptyVariableUnchecked),
      }}
      cancelButtonProps={{
        size: 'small',
      }}
      width={700}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {errors && (
          <>
            <Divider style={{ margin: 0 }}>Errors</Divider>
            <Alert
              message="Definition Errors"
              description={
                <div>
                  We found some errors with the experiment definition:
                  <ul>
                    {errors.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              }
              type="error"
              showIcon
            />
          </>
        )}

        {!(isDomainlessFeatureFlag || isDomainOverlapSupported) &&
          !errors &&
          overlappedExperiments && (
            <Alert
              style={{ marginTop: '10px' }}
              message="Schedule With Approval"
              description={
                <div>
                  You will still be able to schedule the experiment with the
                  current definition, but will have to get approvals from the
                  following conflicted experiments:
                  <ul>
                    {overlappedExperiments?.map((model) => (
                      <li key={model.name}>
                        [{model.state}] {model.name} by {model.owner}
                      </li>
                    ))}
                  </ul>
                </div>
              }
              type="warning"
              showIcon
            />
          )}

        {!errors && !overlappedExperiments && conflicts && (
          <Alert
            style={{ marginTop: '10px' }}
            message="Scheduling Conflicts"
            description={
              <div>
                The population size of your experiments exceeds the available
                population. We found one or more scheduling conflicts with your
                experiment request. Please either manually change the request or
                use our suggested definition. Below are the conflicts we found:
                <ul>
                  {flattenConflicts?.map((experiment) => (
                    <li key={experiment.name}>
                      [{experiment.state}] {experiment.name} by{' '}
                      {experiment.owner}
                    </li>
                  ))}
                </ul>
              </div>
            }
            type="error"
            showIcon
          />
        )}

        {!errors && !conflicts && !overlappedExperiments && success && (
          <Alert
            style={{ marginTop: '10px' }}
            message="Valid Schedule"
            description="No scheduling conflicts were found."
            type="success"
            showIcon
          />
        )}

        {!errors && success && (
          <Alert
            message="Experiment Analysis"
            description={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div>
                  <DeepDive />
                </div>

                {validMetrics.length > 0 && (
                  <p>
                    The interim scorecard will take up to 72 hours to generate.
                    You can check the experiment scorecard on{' '}
                    {calculateScorecardDate(startDate)}.
                    {triggerAnalysis && (
                      <div style={{ marginTop: 8 }}>
                        <Typography.Text
                          type="secondary"
                          style={{ color: 'inherit' }}
                          italic
                        >
                          * Please note that only users who trigger this
                          experiment variable will be considered for generating
                          the Experiment scorecard.
                        </Typography.Text>
                      </div>
                    )}
                  </p>
                )}
              </div>
            }
            type="success"
            showIcon
          />
        )}

        {emptyVariables.length > 0 && (
          <div>
            <Alert
              style={{
                border: '1px solidlinear-gradient(0deg, #F09800, #F09800)',
                background: '#FFF4EB',
              }}
              description={
                <div>
                  The following variable fields are left empty in one or more
                  treatments.
                  <ul
                    style={{
                      marginLeft: 16,
                    }}
                  >
                    {emptyVariables.map((v, i) => (
                      <li key={v + i}>{v}</li>
                    ))}
                  </ul>
                  <Checkbox
                    onChange={handleEmptyVariableClick}
                    style={{
                      background: '#FFF',
                      borderRadius: 12,
                      boxShadow: '0px 4px 9px 0px #161A1D0D',
                      padding: '8px 16px 8px 16px',
                    }}
                  >
                    I understand that these fields were purposely left empty as
                    part of the experiment design.
                  </Checkbox>
                </div>
              }
              type="warning"
            />
          </div>
        )}

        {!errors &&
          (!conflicts || overlappedExperiments) &&
          warnings &&
          warnings.length > 0 && (
            <>
              <Divider style={{ margin: 0 }}>Warnings</Divider>
              <Alert
                message="Experiment Warnings"
                description={
                  <div>
                    We found some warnings with the experiment definition:
                    <ul>
                      {warnings.map((e) => (
                        <li key={e}>{e}</li>
                      ))}
                    </ul>
                  </div>
                }
                type="warning"
                showIcon
              />
            </>
          )}
        {(() => {
          const { sliceWindowDuration, isTimeslice } = getAxaCompatibilityInfo(
            configurations,
            experiment,
          );

          // Only show AXA warnings for timeslice experiments
          if (!isTimeslice) return null;

          const axaWarnings: string[] = [];
          if (isAxaIncompatibleStartTime(startDate)) {
            axaWarnings.push(AXA_WARNING_MESSAGES.startTime);
          }
          if (isAxaIncompatibleSliceWindow(sliceWindowDuration)) {
            axaWarnings.push(AXA_WARNING_MESSAGES.sliceWindow);
          }
          if (axaWarnings.length === 0) return null;
          return (
            <>
              <Divider style={{ margin: 0 }}>AXA Scorecard Warnings</Divider>
              <Alert
                message="AXA Scorecard Compatibility"
                description={
                  <ul>
                    {axaWarnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                }
                type="warning"
                showIcon
              />
            </>
          );
        })()}
      </div>
    </Modal>
  );
};

const ValidateExperiment = () => {
  const experimentContext = useExperimentContext();
  const { experiment, configurations, analysis, validExperiment, validMetric } =
    experimentContext;

  const [open, setOpen] = useState<boolean>(false);

  const form = Form.useFormInstance();
  const experimentDto = toExperimentDto(experiment, configurations);

  const isButtonDisabled = !validExperiment || !validMetric;

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!validExperiment) {
      errors.push('Experiment setup has missing required fields');
    }
    if (!validMetric) {
      const hasPrimary = analysis?.analysisConfig?.primary_metrics?.some((m) =>
        isValidMetric(m),
      );
      const hasOptOut = (experiment?.metadata as any)
        ?.primaryMetricOptOutEnabled;
      if (!hasPrimary && !hasOptOut) {
        errors.push('Select a P0 metric or opt out with a reason');
      } else if (
        hasOptOut &&
        !(experiment?.metadata as any)?.primaryMetricOptOutReason?.trim()
      ) {
        errors.push('Provide an opt-out reason for P0 metric');
      } else {
        errors.push('Metrics configuration has errors');
      }
    }
    return errors;
  }, [validExperiment, validMetric, analysis, experiment]);

  const {
    data: reviewExperimentData,
    mutate: reviewExperimentMutation,
    isPending: isValidating,
  } = useReviewExperiment({
    onSuccess: () => {
      setOpen(true);
    },
    onError: (e) => {
      setOpen(false);
      const errorData = e?.response?.data;
      notification.error({
        message: 'Validate an Experiment',
        description: (
          <>
            <div>Failed to validate the experiment</div>
            {errorData?.arg && <div>Error: {errorData.arg}</div>}
          </>
        ),
      });
    },
  });

  async function reviewExperiment() {
    try {
      reviewExperimentMutation(
        toReviewExperimentDto(experiment, analysis, configurations),
      );

      sendScribeEvent(newCreationFlowEvents.clickValidate.name, {
        name: experiment?.name,
        errors: reviewExperimentData?.errors,
        json_view: JSON.stringify(experimentDto),
        STATE_NAME: newCreationFlowEvents.clickValidate.state,
      });
    } catch (e) {
      notification.error({
        message: 'Validate an Experiment',
        description: 'Failed to validate the experiment',
      });
      return;
    }
  }

  return (
    <>
      <Tooltip
        title={
          isButtonDisabled && validationErrors.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {validationErrors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          ) : undefined
        }
      >
        <Button
          onClick={(_e) => {
            form
              .validateFields({ validateOnly: true })
              .then(() => {
                const experimentStrategy =
                  getAssignmentStrategy(configurations);
                if (isPercentageSplitStrategy(experimentStrategy)) {
                  const variables = getVariables(configurations);
                  const choices = variables[0].choices;
                  const validTotal = validWeightedChoiceTotal(choices);

                  if (validTotal) {
                    reviewExperiment();
                    return;
                  }

                  if (
                    experimentStrategy ===
                      ExperimentAssignmentStrategy.UNIFORM_CHOICE &&
                    validPercentageChoice(choices)
                  ) {
                    reviewExperiment();
                    return;
                  }

                  form.scrollToField([
                    'configurations',
                    '0',
                    'variables',
                    '0',
                    'choices',
                    '0',
                    'chance',
                  ]);
                  return;
                }

                reviewExperiment();
              })

              .catch((e) => {
                form.scrollToField(e.errorFields[0].name, {
                  behavior: 'smooth',
                });
                return;
              });
          }}
          type="secondary"
          size="small"
          disabled={isValidating || isButtonDisabled}
          danger={isButtonDisabled}
        >
          Validate Experiment
        </Button>
      </Tooltip>
      <ReviewModal
        open={open}
        onOk={() => {
          setOpen(false);
        }}
        onCancel={() => {
          setOpen(false);
        }}
        reviewResult={reviewExperimentData}
      />
    </>
  );
};

export default ValidateExperiment;
